import { useEffect, useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchArticleHTML, clearCurrentArticleHTML } from "store/articlesSlice";
import { Helmet } from "react-helmet-async";
import { LineSpinner } from "ldrs/react";
import "ldrs/react/LineSpinner.css";
import s from "./HTMLArticleViewer.module.css";

const HTMLArticleViewer = () => {
  const { hash } = useParams();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const { currentArticleHTML, htmlLoading, htmlError } = useSelector(
    (state) => state.articles
  );
  const containerRef = useRef(null);

  const isFullHTMLDocument =
    currentArticleHTML &&
    (currentArticleHTML.trim().startsWith("<!DOCTYPE") ||
      currentArticleHTML.includes("<html") ||
      currentArticleHTML.includes("<body"));

  useEffect(() => {
    if (hash) {
      dispatch(fetchArticleHTML(hash));
    }

    return () => {
      dispatch(clearCurrentArticleHTML());
    };
  }, [hash, dispatch]);

  // Простая навигация к закладке (без ожидания полной загрузки, только небольшая задержка)
  useEffect(() => {
    if (!currentArticleHTML || htmlLoading || isFullHTMLDocument || !containerRef.current) {
      return;
    }

    const bookmarkId = searchParams.get("bookmark");
    if (!bookmarkId) return;

    // Даем время на вставку HTML в DOM
    setTimeout(() => {
      const element = document.getElementById(bookmarkId);
      if (element) {
        const rect = element.getBoundingClientRect();
        const targetTop = rect.top + window.pageYOffset;

        window.scrollTo({
          top: targetTop,
          behavior: "smooth",
        });
      }
    }, 500);
  }, [currentArticleHTML, htmlLoading, searchParams, isFullHTMLDocument]);

  if (htmlLoading) {
    return (
      <div className={s.loadingOverlay}>
        <div className={s.loadingContent}>
          <LineSpinner size="40" stroke="3" speed="1" color="black" />
          <p className={s.loadingText}>Загрузка статьи...</p>
        </div>
      </div>
    );
  }

  if (htmlError) {
    return (
      <div className={s.container}>
        <div className={s.error}>
          <h2>Ошибка загрузки статьи</h2>
          <p>{htmlError}</p>
        </div>
      </div>
    );
  }

  if (!currentArticleHTML) {
    return (
      <div className={s.container}>
        <div className={s.error}>Статья не найдена</div>
      </div>
    );
  }

  // Определяем, является ли это полным HTML документом
  // Функция для преобразования дефисов в списки
  const convertDashesToLists = (html) => {
    const ENABLE_DASH_LISTS = true; // toggle to test only newline handling

    if (!ENABLE_DASH_LISTS) {
      return html.replace(
        /<p([^>]*)>([\s\S]*?)<\/p>/g,
        (match, attrs, content) => `<p${attrs}>${content.replace(/\r?\n+/g, "<br />")}</p>`
      );
    }

    // Обрабатываем параграфы, которые содержат дефисы на новых строках
    return html.replace(
      /<p([^>]*)>([\s\S]*?)<\/p>/g,
      (match, attrs, content) => {
        // Разделяем содержимое по переносам строк
        const lines = content.split(/\r?\n/);
        
        // Ищем строки, начинающиеся с дефиса
        const dashLines = lines.filter(line => {
          const textOnly = line.replace(/<[^>]+>/g, '').trim();
          // Считаем списком только строки, начинающиеся с "- " (исключаем "3-х")
          return /^-\s+/.test(textOnly);
        });
        
        // Если найдено меньше 1 строки с дефисом, возвращаем параграф,
        // но сохраняем переносы строк через <br />
        if (dashLines.length < 1) {
          const withBreaks = content.replace(/\r?\n+/g, "<br />");
          return `<p${attrs}>${withBreaks}</p>`;
        }
        
        // Разделяем на текст до первого дефиса и элементы списка
        let beforeText = '';
        const listItems = [];
        let foundFirstDash = false;
        
        lines.forEach(line => {
          const textOnly = line.replace(/<[^>]+>/g, '').trim();
          
          if (/^-\s+/.test(textOnly)) {
            foundFirstDash = true;
            // Извлекаем текст после дефиса, сохраняем символы (включая ";")
            const itemText = textOnly.replace(/^-\s+/, '').trim();
            if (itemText) {
              // Сохраняем оригинальную строку, но убираем только префикс "- "
              const originalLine = line.replace(/^[^<]*-\s+/, '').trim();
              listItems.push(originalLine || itemText);
            }
          } else if (!foundFirstDash && line.trim()) {
            // Текст до первого дефиса
            if (beforeText) beforeText += ' ';
            beforeText += line.trim();
          }
        });
        
        if (listItems.length === 0) {
          return match;
        }
        
        // Создаем элементы списка
        const items = listItems
          .filter(item => item.length > 0)
          .map(item => `<li>${item}</li>`)
          .join('');
        
        // Если есть текст перед списком, оставляем его в параграфе
        const beforePart = beforeText.trim() ? `<p${attrs}>${beforeText.trim()}</p>` : '';
        
        // Создаем список
        const listPart = `<ul>${items}</ul>`;
        
        return beforePart + listPart;
      }
    );
  };

  // Извлекаем title из HTML если возможно
  const titleMatch = currentArticleHTML?.match(/<title[^>]*>([^<]+)<\/title>/i);
  const pageTitle = titleMatch ? titleMatch[1] : "Статья";

  // Обрабатываем HTML для преобразования дефисов в списки
  let processedHTML = currentArticleHTML || "";

  if (isFullHTMLDocument) {
    // Обрабатываем полный HTML документ
    processedHTML = convertDashesToLists(processedHTML);
  }

  // Для частичного HTML контента извлекаем только body
  const styleMatch = currentArticleHTML?.match(/<style[^>]*>([\s\S]*?)<\/style>/gi);
  const styles = styleMatch ? styleMatch.join("") : "";

  let bodyContent = currentArticleHTML || "";
  const bodyMatch = currentArticleHTML?.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
  if (bodyMatch) {
    bodyContent = bodyMatch[1];
  } else if (isFullHTMLDocument) {
    // Если нет body тегов, убираем doctype, html, head теги если они есть
    bodyContent = currentArticleHTML
      .replace(/<!DOCTYPE[^>]*>/gi, "")
      .replace(/<html[^>]*>/gi, "")
      .replace(/<\/html>/gi, "")
      .replace(/<head[^>]*>[\s\S]*?<\/head>/gi, "");
  }

  // Преобразуем дефисы в списки
  bodyContent = convertDashesToLists(bodyContent);

  return (
    <div>
      <Helmet prioritizeSeoTags>
        <title>{pageTitle}</title>
        {styles && <style>{styles.replace(/<style[^>]*>/gi, "").replace(/<\/style>/gi, "")}</style>}
      </Helmet>
      <div className={s.container} ref={containerRef}>
        <div
          className={s.htmlContent}
          dangerouslySetInnerHTML={{ __html: bodyContent }}
        />
      </div>
    </div>
  );
};

export default HTMLArticleViewer;

